import React, { useCallback } from 'react';

import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { useRovoNavigation } from '@atlassian/conversation-assistant';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useIntl } from 'react-intl';

import { type CommonSideNavItemProps } from '@townsquare/griffin-navigation/types';
import { useRouterActions } from '@townsquare/router/primitives/actions';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { generatePath } from '@townsquare/router/primitives/path';
import { useRouter } from '@townsquare/router/useRouter';
import { chatRoute } from '@townsquare/routes/chat';
import { useAssistanceServiceParams } from '@townsquare/rovo/hooks';
import { fg } from '@townsquare/stat-sig/gate';

export const RovoTasksNavItem = (props: CommonSideNavItemProps) => {
	const { onClick, item } = props;
	const intl = useIntl();
	const { pushTo } = useRouterActions();
	const [routerStore] = useRouter();
	const { activeMenu } = useUIStore();
	const routeDependencies = useRouteDependencies();

	const assistanceServiceParams = useAssistanceServiceParams({
		experienceId: 'ai-mate',
		touchpointSource: 'townsquare-sidenav',
	});
	const { handleTasksClick } = useRovoNavigation({
		assistanceServiceParams,
		isTasksEnabled: fg('rovo_chat_enable_tasks'),
	});

	const isOnChatRoute = !!matchRoute([chatRoute], routerStore.location.pathname, undefined);
	const isSelected = isOnChatRoute && activeMenu === 'tasks';

	const chatHref = generatePath(chatRoute, routeDependencies);

	const onTasksClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			pushTo(chatRoute, {
				query: {
					rovoChatPathway: 'chat',
				},
			});
			// Trigger the tasks panel to open
			handleTasksClick();
			onClick(item, e);
		},
		[pushTo, handleTasksClick, item, onClick],
	);

	return (
		<LinkMenuItem
			href={chatHref}
			elemBefore={<ScorecardIcon label="" color="currentColor" />}
			onClick={onTasksClick}
			isSelected={isSelected}
			testId="side-nav-tasks"
		>
			{intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.tasks',
				description: 'Side nav button to open tasks page',
				defaultMessage: 'Tasks',
			})}
		</LinkMenuItem>
	);
};
